import type { AwardCriterionDto, EventDataDto, EventDataPatchDto, TemplateContentDto } from "../../autogen/bff-api";
import { toCriteriaPatch } from "../../pages/sourcing-events/buyer/wizard/utils/awardCriteriaPatch";
import type { TemplateSection } from "./templateFromEvent";

export const templateDefinesSection = (content: TemplateContentDto, section: TemplateSection): boolean => {
  switch (section) {
    case "minimum":
      return (content.minimumRequirements?.length ?? 0) > 0;
    case "selection":
      return (content.selectionCriteria?.length ?? 0) > 0;
    case "award":
      return content.awardCriteria != null;
  }
};

export const criteriaReplacedByTemplate = (
  content: TemplateContentDto,
  eventData: EventDataDto
): AwardCriterionDto[] =>
  content.awardCriteria ? (eventData.awardCriteria?.bestPriceQualityRatio?.criteria ?? []) : [];

export const templateApplyPatch = (content: TemplateContentDto, eventData: EventDataDto): EventDataPatchDto => {
  const patch: EventDataPatchDto = {};

  if (content.minimumRequirements && content.minimumRequirements.length > 0) {
    patch.minimumRequirements = content.minimumRequirements.map((r) => ({
      id: undefined,
      title: r.title,
      description: r.description,
      responseType: r.responseType,
    }));
    // Krav that arrive from a template would land in a step the buyer never switched on, so the
    // template switches it on for them.
    patch.includeMinimumRequirements = true;
  }

  if (content.selectionCriteria && content.selectionCriteria.length > 0) {
    patch.selectionCriteria = content.selectionCriteria.map((c) => ({ ...c, id: undefined }));
  }

  if (content.awardCriteria?.bestPriceQualityRatio) {
    // Fresh ids at both levels: two procurements applying the same template must not share criterion or
    // sub-criterion ids, since evaluation scores and award-letter justifications are keyed by them.
    const criteria = (content.awardCriteria.bestPriceQualityRatio.criteria ?? []).map((c) => ({
      ...c,
      id: undefined,
      subCriteria: (c.subCriteria ?? []).map((s) => ({ ...s, id: undefined })),
    }));
    patch.awardCriteria = { lowestPrice: null, bestPriceQualityRatio: { criteria: toCriteriaPatch(criteria) } };
  } else if (content.awardCriteria?.lowestPrice) {
    patch.awardCriteria = { lowestPrice: { selected: true }, bestPriceQualityRatio: null };
  }

  if (content.awardCriteria) patch.includeAwardCriteria = true;

  if (content.awardCriteria && content.priceModel) {
    patch.priceModel = {
      proportional: content.priceModel.proportional ? { selected: true } : null,
      linear: content.priceModel.linear ? { selected: true } : null,
      hybrid: content.priceModel.hybrid ? { breakpointPercent: content.priceModel.hybrid.breakpointPercent } : null,
    };
  }

  return patch;
};
